import { RecurrenceDto, RecurrenceDtoFrequencyEnum } from 'src/Services/API/generated';

enum GENRE {
  MASCULINE,
  FEMININE,
}

const FREQ_MAP = [
  { label: 'ans', genre: GENRE.MASCULINE },
  { label: 'mois', genre: GENRE.MASCULINE },
  { label: 'semaines', genre: GENRE.FEMININE },
  { label: 'jours', genre: GENRE.MASCULINE },
  { label: 'heures', genre: GENRE.FEMININE },
  { label: 'minutes', genre: GENRE.FEMININE },
  { label: 'secondes', genre: GENRE.FEMININE },
];

const FREQ_TO_INDEX = {
  YEARLY: 0,
  MONTHLY: 1,
  WEEKLY: 2,
  DAILY: 3,
};

const WEEKDAY_MAP = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];

const MONTH_MAP = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];

export const humanizeRRule = (rule: RecurrenceDto) => {
  const parts = [];

  if (rule.frequency) {
    const interval = rule.interval || 1;
    const index = FREQ_TO_INDEX[rule.frequency as keyof typeof FREQ_TO_INDEX];
    if (FREQ_MAP[index].genre === GENRE.MASCULINE) {
      if (interval === 1) {
        parts.push(`Tous les ${FREQ_MAP[index].label}`);
      } else {
        parts.push(`Tous les ${interval} ${FREQ_MAP[index].label}`);
      }
    } else {
      if (interval === 1) {
        parts.push(`Toutes les ${FREQ_MAP[index].label}`);
      } else {
        parts.push(`Toutes les ${interval} ${FREQ_MAP[index].label}`);
      }
    }
  }

  if (rule.byWeekDay) {
    const weekdays = Array.isArray(rule.byWeekDay) ? rule.byWeekDay : [rule.byWeekDay];
    const weekdayNames = weekdays.map(day => WEEKDAY_MAP[day]);

    if (weekdayNames.length === 1) {
      parts.push(`le ${weekdayNames[0]}`);
    } else {
      parts.push(
        `les ${weekdayNames.slice(0, -1).join(', ')} et ${weekdayNames[weekdayNames.length - 1]}`
      );
    }
  }

  if (rule.byMonthDay) {
    const days = [rule.byMonthDay];
    if (days.length > 0) {
      if (days.length === 1) {
        parts.push(`le ${days[0]} du mois`);
      } else {
        parts.push(`les ${days.slice(0, -1).join(', ')} et ${days[days.length - 1]} du mois`);
      }
    }
  }

  if (rule.byMonth) {
    const months = Array.isArray(rule.byMonth) ? rule.byMonth : [rule.byMonth];
    if (months.length === 1) {
      parts.push(`en ${MONTH_MAP[months[0] - 1]}`);
    } else {
      parts.push(
        `en ${months
          .slice(0, -1)
          .map(m => MONTH_MAP[m - 1])
          .join(', ')} et ${MONTH_MAP[months[months.length - 1] - 1]}`
      );
    }
  }

  return parts.join(' ');
};

export function isRecurrenceDto(obj: unknown): obj is RecurrenceDto {
  if (!obj || typeof obj !== 'object' || obj === null) {
    return false;
  }

  const candidate = obj as Partial<RecurrenceDto>;

  if (
    !('frequency' in candidate) ||
    !Object.values(RecurrenceDtoFrequencyEnum).includes(
      candidate.frequency as RecurrenceDtoFrequencyEnum
    )
  ) {
    return false;
  }

  if (!('timezone' in candidate) || typeof candidate.timezone !== 'string') {
    return false;
  }

  if (
    'interval' in candidate &&
    (typeof candidate.interval !== 'number' || candidate.interval <= 0)
  ) {
    return false;
  }

  if ('byMonthDay' in candidate) {
    if (!Array.isArray(candidate.byMonthDay)) {
      return false;
    }
    if (!candidate.byMonthDay.every(day => typeof day === 'number' && day >= 1 && day <= 31)) {
      return false;
    }
  }

  if ('byMonth' in candidate) {
    if (!Array.isArray(candidate.byMonth)) {
      return false;
    }
    if (!candidate.byMonth.every(month => typeof month === 'number' && month >= 1 && month <= 12)) {
      return false;
    }
  }

  if ('byWeekDay' in candidate) {
    if (!Array.isArray(candidate.byWeekDay)) {
      return false;
    }
    if (!candidate.byWeekDay.every(day => typeof day === 'number' && day >= 0 && day <= 6)) {
      return false;
    }
  }

  if ('bySetPos' in candidate && typeof candidate.bySetPos !== 'number') {
    return false;
  }

  return true;
}
